import React, { useMemo } from 'react';

import { Box } from '@chakra-ui/react';

import { Plus } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';
import { SplitButton } from 'uiToolkitV1/SplitButton';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useBulkActionContext } from './BulkActionContext';

export interface BulkActionHeaderProps {
  clearSelection: () => void;
  selectAll: () => void;
  onAddRow: () => void;
  activeTab?: string;
}

// Move handlers outside the component to prevent recreation
let selectedRowIdsRef: (string | number)[] = [];

const handleApproveAll = () => {
  console.log('Approve all selected:', selectedRowIdsRef);
  // TODO: Implement approve logic
};

const handleDenyAll = () => {
  console.log('Deny all selected:', selectedRowIdsRef);
  // TODO: Implement deny logic
};

const handleSendBackToProposer = () => {
  console.log('Send back to proposer all selected:', selectedRowIdsRef);
  // TODO: Implement send back to proposer logic
};

const publishActionsHandlePublishToPayroll = () => {
  console.log('Publish to payroll all selected:', selectedRowIdsRef);
  // TODO: Implement publish to payroll logic
};

const publishActionsHandleDeny = () => {
  console.log('Deny all selected:', selectedRowIdsRef);
  // TODO: Implement deny logic
};

const publishActionsHandleSendBackToProposer = () => {
  console.log('Send back to proposer all selected:', selectedRowIdsRef);
  // TODO: Implement send back to proposer logic
};

const publishActionsHandleOnboarding = () => {
  console.log('Onboarding all selected:', selectedRowIdsRef);
  // TODO: Implement onboarding logic
};

// Create stable action arrays outside the component
const PROPOSAL_ACTIONS = [
  {
    value: 'Submit for approval',
    label: 'Submit for approval',
    onClick: handleApproveAll,
  },
];

const APPROVE_ACTIONS = [
  {
    value: 'Approve',
    label: 'Approve',
    onClick: handleApproveAll,
  },
  {
    value: 'Deny',
    label: 'Deny',
    onClick: handleDenyAll,
  },
  {
    value: 'Send back to proposer',
    label: 'Send back to proposer',
    onClick: handleSendBackToProposer,
  },
];

const PUBLISH_ACTIONS = [
  {
    value: 'Publish to payroll',
    label: 'Publish to payroll',
    onClick: publishActionsHandlePublishToPayroll,
  },
  {
    value: 'Deny',
    label: 'Deny',
    onClick: publishActionsHandleDeny,
  },
  {
    value: 'Send back to proposer',
    label: 'Send back to proposer',
    onClick: publishActionsHandleSendBackToProposer,
  },
  {
    value: 'Onboarding',
    label: 'Onboarding',
    onClick: publishActionsHandleOnboarding,
  },
];

export const BulkActionHeader: React.FC<BulkActionHeaderProps> = ({
  clearSelection,
  selectAll,
  onAddRow,
  activeTab,
}) => {
  const { selectedRowIds, totalRows } = useBulkActionContext();
  const totalRowsSelected = selectedRowIds.length;

  // Update the ref when selectedRowIds changes
  React.useEffect(() => {
    selectedRowIdsRef = selectedRowIds;
  }, [selectedRowIds]);

  // Create stable action arrays that only change when activeTab changes
  const actions = useMemo(() => {
    if (activeTab === 'propose') {
      return PROPOSAL_ACTIONS;
    }

    if (activeTab === 'publish') {
      return PUBLISH_ACTIONS;
    }

    return APPROVE_ACTIONS;
  }, [activeTab]);

  return totalRows > 0 ? (
    <Box display="flex" alignItems="center" mb={3}>
      <Box
        display="flex"
        px="8px"
        py="6px"
        alignItems="center"
        bgColor="neutral.F5F7F9"
        borderWidth="1px"
        borderColor="neutral.EBEFF3"
        borderRadius="4px"
        flex={1}
      >
        {totalRows === totalRowsSelected ? (
          <TextBodyMedium bold ml="8px">
            All {totalRowsSelected > 1 ? `${totalRowsSelected} ` : ''} rows are selected
          </TextBodyMedium>
        ) : (
          totalRowsSelected > 0 && (
            <TextBodyMedium bold ml="8px">
              Total {totalRowsSelected} {totalRowsSelected > 1 ? 'rows are' : 'row is'} selected
            </TextBodyMedium>
          )
        )}
        {totalRows !== totalRowsSelected && (
          <Button variant="tertiary" onClick={selectAll} p="0 !important" m="0 !important">
            Select all {totalRows} rows
          </Button>
        )}
        {totalRowsSelected > 0 && (
          <Button variant="tertiary" onClick={clearSelection}>
            Clear Selection
          </Button>
        )}
        {actions.length > 1 ? (
          <Box ml="auto">
            <SplitButton
              buttonProps={{
                variant: 'filled',
                withBackground: false,
                style: { padding: '8px', borderRadius: '4px 0px 0px 4px', height: '40px' },
                isDisabled: totalRowsSelected === 0,
              }}
              menuButtonProps={{
                variant: 'filled',
                withBackground: false,
                style: { padding: '8px', borderRadius: '0px 4px 4px 0px', height: '40px' },
              }}
              options={actions}
              isDisabled={totalRowsSelected === 0}
            />
          </Box>
        ) : (
          <Box ml="auto">
            <Button onClick={actions[0].onClick} isDisabled={totalRowsSelected === 0}>
              {actions[0].label}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  ) : null;
};
